/**
 * Cleo Can 54: a helper Cleo starts is its own read-only conversation.
 *
 * A native helper (a Claude Task subagent, a Codex subagent) is projected by
 * the server into a child thread of the conversation that started it. The
 * thread is marked with {@link ThreadHelper}, carries what the helper was asked
 * (a user message), what it did (`helper.step` activities), what it said (an
 * assistant message) and one `helper.status` activity that holds its state.
 * No engine session is ever started for it.
 *
 * Everything here is pure so the server projection, the decider and every
 * client read a helper the same way.
 */
import * as Schema from "effect/Schema";

import { ThreadId, TrimmedNonEmptyString } from "./baseSchemas.ts";

export const HELPER_STATUS_ACTIVITY_KIND = "helper.status";
export const HELPER_STEP_ACTIVITY_KIND = "helper.step";
/** Steps kept per helper conversation; later ones are not recorded. */
export const HELPER_STEP_LIMIT = 200;

/** What a person is told when they try to write to a helper's conversation. */
export const HELPER_READ_ONLY_DETAIL =
  "This is a helper's conversation and it is read only. Message the conversation that started it instead.";

/** The mark on a helper thread: which engine's helper it is and its id there. */
export const ThreadHelper = Schema.Struct({
  engine: TrimmedNonEmptyString,
  helperId: TrimmedNonEmptyString,
  role: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
});
export type ThreadHelper = typeof ThreadHelper.Type;

export const HelperStatus = Schema.Literals(["working", "finished", "failed", "stopped"]);
export type HelperStatus = typeof HelperStatus.Type;

export function isTerminalHelperStatus(status: HelperStatus): boolean {
  return status !== "working";
}

function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * The helper's thread id: deterministic from the parent thread and the
 * engine's helper id, so a replay or a restart finds the same thread.
 */
export function helperThreadId(parentThreadId: string, engine: string, helperId: string): ThreadId {
  const key = `${parentThreadId}\u0000${engine}\u0000${helperId}`;
  const safe = (value: string) => value.replace(/[^A-Za-z0-9_-]/g, "_").slice(0, 48);
  return ThreadId.make(`helper-${safe(parentThreadId)}-${safe(helperId)}-${fnv1a(key)}`);
}
export const helperAskMessageId = (threadId: string) => `helper-ask:${threadId}`;
export const helperResultMessageId = (threadId: string) => `helper-result:${threadId}`;
export const helperStatusActivityId = (threadId: string) => `helper-status:${threadId}`;

export interface HelperStatusRecord {
  readonly status: HelperStatus;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly error: string | null;
  readonly effort: string | null;
  /** Whole milliseconds from start to end; null while it is working. */
  readonly durationMs: number | null;
}

interface ActivityLike {
  readonly kind: string;
  readonly payload?: unknown;
}

const STATUSES: ReadonlySet<string> = new Set(["working", "finished", "failed", "stopped"]);

/** A helper conversation's state, read from its `helper.status` activity. */
export function readHelperStatus(
  activities: ReadonlyArray<ActivityLike>,
): HelperStatusRecord | null {
  for (let index = activities.length - 1; index >= 0; index -= 1) {
    const activity = activities[index];
    if (!activity || activity.kind !== HELPER_STATUS_ACTIVITY_KIND) continue;
    const payload = activity.payload;
    if (typeof payload !== "object" || payload === null) continue;
    const record = payload as Record<string, unknown>;
    if (typeof record.status !== "string" || !STATUSES.has(record.status)) continue;
    if (typeof record.startedAt !== "string") continue;
    const endedAt = typeof record.endedAt === "string" ? record.endedAt : null;
    const started = Date.parse(record.startedAt);
    const ended = endedAt === null ? Number.NaN : Date.parse(endedAt);
    return {
      status: record.status as HelperStatus,
      startedAt: record.startedAt,
      endedAt,
      error: typeof record.error === "string" ? record.error : null,
      effort: typeof record.effort === "string" ? record.effort : null,
      durationMs:
        Number.isFinite(started) && Number.isFinite(ended) ? Math.max(0, ended - started) : null,
    };
  }
  return null;
}
